import React from "react";

export const COLOR_MAP: Record<string, string> = {
  black: "#111827",
  "space black": "#1F2937",
  graphite: "#383838",
  midnight: "#1E293B",
  white: "#FFFFFF",
  starlight: "#F5F5DC",
  silver: "#CBD5E1",
  gray: "#64748B",
  grey: "#64748B",
  "space gray": "#475569",
  "space grey": "#475569",
  red: "#EF4444",
  blue: "#2563EB",
  "midnight blue": "#1E3A8A",
  navy: "#1E3A8A",
  "sky blue": "#0284C7",
  green: "#16A34A",
  "midnight green": "#14532D",
  yellow: "#EAB308",
  gold: "#F59E0B",
  "rose gold": "#F472B6",
  pink: "#EC4899",
  purple: "#8B5CF6",
  violet: "#7C3AED",
  orange: "#F97316",
  brown: "#78350F",
  beige: "#D4B996",
  cream: "#FFFDD0",
  teal: "#0D9488",
  cyan: "#0891B2",
  dark: "#1E293B",
  light: "#F1F5F9",
};

export type ResolvableOrderItem = {
  title?: string;
  variantId?: string | null;
  variantTitle?: string | null;
  sku?: string | null;
  product?: {
    id?: string;
    title?: string;
    category?: string;
    brand?: string;
    variants?: any[];
    specifications?: Record<string, any>;
  } | null;
};

export interface ResolvedVariantInfo {
  color?: string;
  colorCode?: string;
  size?: string;
  variantName?: string;
  sku?: string;
  hasSpecificVariant: boolean;
}

export function resolveItemVariant(item: ResolvableOrderItem): ResolvedVariantInfo {
  let color: string | undefined;
  let colorCode: string | undefined;
  let size: string | undefined;
  let sku: string | undefined = item.sku || undefined;
  let variantName: string | undefined = item.variantTitle?.trim() || undefined;

  const variants = item.product?.variants || [];

  // 1. Try matching variant object from product catalog
  const matched =
    (item.variantId ? variants.find((v) => v.id === item.variantId) : null) ||
    (item.sku ? variants.find((v) => v.sku === item.sku) : null) ||
    (variantName ? variants.find((v) => v.title === variantName || v.color === variantName) : null);

  if (matched) {
    if (matched.color) color = matched.color;
    if (matched.colorCode) colorCode = matched.colorCode;
    if (matched.size) size = matched.size;
    if (matched.sku && !sku) sku = matched.sku;
    if (!variantName && matched.title) variantName = matched.title;

    if (!color && matched.attributes?.color) color = String(matched.attributes.color);
    if (!color && matched.attributes?.Color) color = String(matched.attributes.Color);
    if (!size && matched.attributes?.size) size = String(matched.attributes.size);
    if (!size && matched.attributes?.Size) size = String(matched.attributes.Size);
  }

  // 2. Parse from variantTitle string (e.g. "Color: Space Black · Size: M", "Color: Red", "Space Gray", etc.)
  if (variantName) {
    if (!color) {
      const colorMatch = variantName.match(/color:\s*([^,;·|]+)/i);
      if (colorMatch) {
        color = colorMatch[1].trim();
      }
    }
    if (!size) {
      const sizeMatch = variantName.match(/size:\s*([^,;·|]+)/i);
      if (sizeMatch) {
        size = sizeMatch[1].trim();
      }
    }
    // Check if variantTitle is directly a known color or contains one
    if (!color) {
      const lower = variantName.toLowerCase();
      if (COLOR_MAP[lower]) {
        color = variantName;
      } else {
        // Look for multi-word or single-word color in variantName
        for (const key of Object.keys(COLOR_MAP)) {
          if (new RegExp(`\\b${key}\\b`, "i").test(lower)) {
            color = variantName;
            colorCode = COLOR_MAP[key];
            break;
          }
        }
      }
    }
  }

  // 3. Fallback: Parse from product title if title has "(Black)" or "- White" or similar
  if (!color && item.title) {
    const parentheticalMatch = item.title.match(/\(([^)]+)\)/);
    if (parentheticalMatch) {
      const inner = parentheticalMatch[1].trim().toLowerCase();
      if (COLOR_MAP[inner]) {
        color = parentheticalMatch[1].trim();
      } else {
        const colorInParen = inner.match(/\b(space black|space gray|space grey|midnight blue|rose gold|midnight green|black|white|silver|gray|grey|red|blue|green|gold|purple|yellow|orange|pink|teal|navy)\b/i);
        if (colorInParen) {
          color = colorInParen[1];
        }
      }
    }

    if (!color) {
      const titleColorMatch = item.title.match(/[-–]\s*(space black|space gray|space grey|midnight blue|rose gold|midnight green|black|white|silver|gray|grey|red|blue|green|gold|purple|yellow|orange|pink|teal|navy)\b/i);
      if (titleColorMatch) {
        color = titleColorMatch[1];
      }
    }
  }

  // 4. Resolve color code hex if we have a color name
  if (color) {
    const cleanName = color.trim().toLowerCase();
    if (!colorCode && COLOR_MAP[cleanName]) {
      colorCode = COLOR_MAP[cleanName];
    } else if (!colorCode) {
      // Substring search in COLOR_MAP
      for (const [key, hex] of Object.entries(COLOR_MAP)) {
        if (new RegExp(`\\b${key}\\b`, "i").test(cleanName)) {
          colorCode = hex;
          break;
        }
      }
    }
    if (!colorCode && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
      colorCode = color;
    }
    // Capitalize color display name nicely (e.g. "space gray" -> "Space Gray")
    color = color
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const hasSpecificVariant = Boolean(color || size || (variantName && !variantName.toLowerCase().includes("default")));

  return {
    color,
    colorCode: colorCode || (color ? "#64748B" : undefined),
    size,
    variantName,
    sku,
    hasSpecificVariant,
  };
}

interface ItemVariantBadgeProps {
  item: ResolvableOrderItem;
  size?: "sm" | "md";
  showDefaultFallback?: boolean;
  className?: string;
}

export function ItemVariantBadge({
  item,
  size = "sm",
  showDefaultFallback = false,
  className = "",
}: ItemVariantBadgeProps) {
  const info = resolveItemVariant(item);

  if (!info.hasSpecificVariant) {
    if (!showDefaultFallback) return null;
    return (
      <span
        className={`inline-flex items-center gap-1 rounded bg-slate-100/80 px-1.5 py-0.5 font-medium text-slate-500 dark:bg-white/5 dark:text-slate-400 ${
          size === "sm" ? "text-[10px]" : "text-xs"
        } ${className}`}
      >
        <span>Standard Edition</span>
      </span>
    );
  }

  const isLightColor =
    info.colorCode &&
    ["#ffffff", "#fff", "#f1f5f9", "#cbd5e1", "#fffdd0", "#f5f5dc"].includes(
      info.colorCode.toLowerCase()
    );

  return (
    <div className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      {info.color && (
        <span
          className={`inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white font-bold text-slate-900 shadow-2xs dark:border-white/10 dark:bg-slate-800 dark:text-white ${
            size === "sm" ? "px-1.5 py-0.5 text-[10.5px]" : "px-2 py-0.5 text-xs"
          }`}
          title={`Selected Color: ${info.color}`}
        >
          <span
            className={`inline-block shrink-0 rounded-full border ${
              isLightColor ? "border-slate-400" : "border-black/20"
            } ${size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3"}`}
            style={{ backgroundColor: info.colorCode || "#64748B" }}
          />
          <span>Color: <strong className="font-extrabold text-slate-950 dark:text-white">{info.color}</strong></span>
        </span>
      )}

      {info.size && (
        <span
          className={`inline-flex items-center rounded-md border border-slate-200 bg-slate-100 font-semibold text-slate-800 dark:border-white/10 dark:bg-slate-700 dark:text-slate-200 ${
            size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
          }`}
          title={`Selected Size: ${info.size}`}
        >
          Size: <strong className="ml-1 font-bold">{info.size}</strong>
        </span>
      )}

      {!info.color && !info.size && info.variantName && (
        <span
          className={`inline-flex items-center rounded-md border border-slate-200 bg-slate-100 font-semibold text-slate-700 dark:border-white/10 dark:bg-slate-700 dark:text-slate-200 ${
            size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
          }`}
        >
          {info.variantName}
        </span>
      )}
    </div>
  );
}
